import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { gsap } from "gsap";
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  Shield,
  Loader2,
  ArrowRight,
  Users,
  TrendingUp,
  Star,
  CheckCircle,
} from "lucide-react";
import GooglePopup from "./ui/GooglePopupLogin";


const Login = () => {
  const {
    login,
    loading: globalLoading,
    sendVerificationEmail,
    user,
    profile,
    isRoleLoading,
    isEmailVerified,
  } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [gridDimensions, setGridDimensions] = useState({ rows: 0, cols: 0 });
  const [loginAttempted, setLoginAttempted] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Refs for animations
  const containerRef = useRef(null);
  const leftPanelRef = useRef(null);
  const rightPanelRef = useRef(null);
  const formRef = useRef(null);

  // Handle messages from EmailVerificationRequired page
  useEffect(() => {
    if (location.state?.message) {
      setSuccess(location.state.message);
      // Clear the message after showing it
      window.history.replaceState({}, document.title);
    }
    if (location.state?.email) {
      setEmail(location.state.email);
    }
  }, [location.state]);

  // Handle post-login navigation after profile is loaded
  useEffect(() => {
    if (loginAttempted && user && !isRoleLoading && profile !== null) {
      console.log("🔍 Post-login navigation check:", {
        user: user.id,
        isEmailVerified,
        isRoleLoading,
        profile: profile?.email_verified,
      });

      // Use the profile data directly to avoid race conditions
      const emailVerified = profile?.email_verified || false;

      if (emailVerified) {
        // Email is verified, proceed to homepage which will handle role-based redirect
        console.log(
          "✅ Email verified, navigating to homepage for role-based redirect"
        );
        navigate("/");
      } else {
        // Email not verified, redirect to verification required page
        console.log("❌ Email not verified, redirecting to verification page");
        navigate("/verify-email-required", {
          state: { email: user.email },
        });
      }

      setLoginAttempted(false);
      setLoading(false);
    }
  }, [loginAttempted, user, isRoleLoading, isEmailVerified, navigate, profile]);

  useEffect(() => {
    // Set grid dimensions after component mounts
    const updateGridDimensions = () => {
      setGridDimensions({
        rows: Math.ceil(window.innerHeight / 40),
        cols: Math.ceil(window.innerWidth / 40),
      });
    };

    updateGridDimensions();
    window.addEventListener("resize", updateGridDimensions);

    return () => window.removeEventListener("resize", updateGridDimensions);
  }, []);

  useEffect(() => {
    // Initial animations
    const tl = gsap.timeline();

    // Set initial states
    gsap.set([leftPanelRef.current, rightPanelRef.current], {
      opacity: 0,
      x: -50,
    });

    gsap.set(rightPanelRef.current, {
      x: 50,
    });

    // Animate in
    tl.to([leftPanelRef.current, rightPanelRef.current], {
      opacity: 1,
      x: 0,
      duration: 0.8,
      ease: "power3.out",
      stagger: 0.2,
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      setError("");
      setSuccess("");

      console.log("🔐 Attempting login for:", email);
      const result = await login(email, password);

      if (result.success) {
        console.log("✅ Login successful, waiting for profile to load...");
        // Don't navigate immediately - let useEffect handle navigation after profile loads
        setLoginAttempted(true);
        // Don't set loading to false here - let useEffect handle it
      }
    } catch (error) {
      console.error("Login error:", error);
      if (error.message?.includes("Invalid login credentials")) {
        setError(
          "Invalid email or password. Please check your credentials and try again."
        );
      } else if (error.message?.includes("Email not confirmed")) {
        // Redirect to verification required page for unconfirmed accounts
        navigate("/verify-email-required", {
          state: { email: email },
        });
        return;
      } else {
        setError(error.message || "Login failed. Please try again.");
      }
      setLoading(false);
    }
  };

  return (
    <div
      ref={containerRef}
      className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center relative overflow-hidden"
    >
      {/* Grid background with center glow effect */}
      <div className="absolute inset-0">
        {/* Base grid pattern */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
            `,
            backgroundSize: "40px 40px",
          }}
        />

        {/* Radial gradient mask to create center glow and edge fade effect */}
        <div
          className="absolute inset-0"
          style={{
            background: `
              radial-gradient(circle at 50% 50%, 
                rgba(184, 70, 14, 0.15) 0%, 
                rgba(184, 70, 14, 0.08) 40%, 
                rgba(184, 70, 14, 0.03) 70%,
                rgba(184, 70, 14, 0) 100%
              )
            `,
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)
            `,
            backgroundSize: "40px 40px",
            maskImage: `
              radial-gradient(circle at 50% 50%, 
                rgba(255,255,255,1) 0%, 
                rgba(255,255,255,0.8) 30%, 
                rgba(255,255,255,0.3) 60%,
                rgba(255,255,255,0.1) 80%,
                rgba(255,255,255,0) 100%
              )
            `,
            WebkitMaskImage: `
              radial-gradient(circle at 50% 50%, 
                rgba(255,255,255,1) 0%, 
                rgba(255,255,255,0.8) 30%, 
                rgba(255,255,255,0.3) 60%,
                rgba(255,255,255,0.1) 80%,
                rgba(255,255,255,0) 100%
              )
            `,
          }}
        />

        {/* Interactive grid overlay for hover effects */}
        <div className="absolute inset-0 pointer-events-none">
          {Array.from({ length: gridDimensions.rows }).map((_, row) =>
            Array.from({ length: gridDimensions.cols }).map((_, col) => (
              <div
                key={`${row}-${col}`}
                className="absolute w-10 h-10 pointer-events-auto transition-all duration-300"
                style={{
                  left: `${col * 40}px`,
                  top: `${row * 40}px`,
                  animation: "none",
                }}
                onMouseEnter={(e) => {
                  gsap.fromTo(
                    e.target,
                    {
                      backgroundColor: "rgba(184, 70, 14, 0)",
                      boxShadow: "0 0 0px rgba(184, 70, 14, 0)",
                      scale: 1,
                    },
                    {
                      backgroundColor: "rgba(184, 70, 14, 0.3)",
                      boxShadow:
                        "0 0 25px rgba(184, 70, 14, 0.8), inset 0 0 15px rgba(184, 70, 14, 0.3)",
                      scale: 1.05,
                      duration: 0.2,
                      ease: "power2.out",
                    }
                  );

                  const adjacentSquares = document.querySelectorAll(
                    `[data-row="${row}"][data-col="${
                      col - 1
                    }"], [data-row="${row}"][data-col="${
                      col + 1
                    }"], [data-row="${
                      row - 1
                    }"][data-col="${col}"], [data-row="${
                      row + 1
                    }"][data-col="${col}"]`
                  );
                  adjacentSquares.forEach((square, index) => {
                    gsap.fromTo(
                      square,
                      {
                        backgroundColor: "rgba(184, 70, 14, 0)",
                        boxShadow: "0 0 0px rgba(184, 70, 14, 0)",
                      },
                      {
                        backgroundColor: "rgba(184, 70, 14, 0.1)",
                        boxShadow: "0 0 10px rgba(184, 70, 14, 0.4)",
                        duration: 0.15,
                        delay: index * 0.05,
                        ease: "power2.out",
                        onComplete: () => {
                          gsap.to(square, {
                            backgroundColor: "rgba(184, 70, 14, 0)",
                            boxShadow: "0 0 0px rgba(184, 70, 14, 0)",
                            duration: 0.3,
                            ease: "power2.out",
                          });
                        },
                      }
                    );
                  });
                }}
                onMouseLeave={(e) => {
                  gsap.to(e.target, {
                    backgroundColor: "rgba(184, 70, 14, 0)",
                    boxShadow: "0 0 0px rgba(184, 70, 14, 0)",
                    scale: 1,
                    duration: 0.4,
                    ease: "power2.out",
                  });
                }}
                data-row={row}
                data-col={col}
              />
            ))
          )}
        </div>
      </div>

      {/* Main Centered Container */}
      <div className="relative z-10 w-full max-w-5xl mx-auto p-4 sm:p-6 lg:p-8">
        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl lg:rounded-3xl shadow-2xl overflow-hidden">
          <div className="flex flex-col lg:flex-row">
            {/* Left Panel - Login Form */}
            <div
              ref={leftPanelRef}
              className="w-full lg:w-1/2 p-4 sm:p-6 lg:p-8 flex items-center justify-center order-2 lg:order-1"
            >
              <div className="w-full max-w-sm">
                <div ref={formRef}>
                  {/* Logo */}
                  <div className="flex justify-center mb-4">
                    <div className="w-12 h-12 lg:w-14 lg:h-14 bg-gradient-to-br from-[#B8460E] via-[#8B3A0E] to-[#B8460E] rounded-2xl flex items-center justify-center shadow-lg">
                      <Shield className="w-6 h-6 lg:w-7 lg:h-7 text-white" />
                    </div>
                  </div>

                  {/* Title */}
                  <div className="text-center mb-6">
                    <h1 className="text-2xl lg:text-3xl font-bold text-white mb-1">
                      Welcome back
                    </h1>
                    <p className="text-sm lg:text-base text-gray-300">
                      Sign in to your account
                    </p>
                  </div>

                  {/* Login Form */}
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {success && (
                      <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-2.5 rounded-xl text-sm flex items-center">
                        <CheckCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                        {success}
                      </div>
                    )}

                    {error && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2.5 rounded-xl text-sm">
                        {error}
                      </div>
                    )}

                    <div>
                      <label className="block text-gray-300 text-sm font-semibold mb-1.5">
                        Email
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 lg:w-5 lg:h-5" />
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="w-full bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl pl-10 lg:pl-12 pr-4 py-2.5 lg:py-3 text-sm lg:text-base text-white placeholder-gray-400 focus:outline-none focus:border-[#B8460E] focus:ring-2 focus:ring-[#B8460E]/20 transition-all duration-200"
                          placeholder="Enter your email"
                          required
                          autoFocus
                          disabled={loading || globalLoading}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-gray-300 text-sm font-semibold mb-1.5">
                        Password
                      </label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 lg:w-5 lg:h-5" />
                        <input
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="w-full bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl pl-10 lg:pl-12 pr-10 lg:pr-12 py-2.5 lg:py-3 text-sm lg:text-base text-white placeholder-gray-400 focus:outline-none focus:border-[#B8460E] focus:ring-2 focus:ring-[#B8460E]/20 transition-all duration-200"
                          placeholder="Enter your password"
                          required
                          disabled={loading || globalLoading}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-200 transition-colors"
                        >
                          {showPassword ? (
                            <EyeOff className="w-4 h-4 lg:w-5 lg:h-5" />
                          ) : (
                            <Eye className="w-4 h-4 lg:w-5 lg:h-5" />
                          )}
                        </button>
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={loading || globalLoading}
                      className="w-full bg-gradient-to-r from-[#B8460E] via-[#8B3A0E] to-[#B8460E] hover:from-[#D4541A] hover:via-[#A0421A] hover:to-[#D4541A] disabled:opacity-50 disabled:cursor-not-allowed text-white py-2.5 lg:py-3 px-6 rounded-xl transition-all duration-200 font-semibold flex items-center justify-center text-sm lg:text-base shadow-lg hover:shadow-xl relative overflow-hidden group"
                    >
                      {loading || globalLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 lg:w-5 lg:h-5 mr-2 animate-spin" />
                          Signing in...
                        </>
                      ) : (
                        <>
                          Sign in
                          <ArrowRight className="w-4 h-4 lg:w-5 lg:h-5 ml-2" />
                        </>
                      )}
                    </button>
                  </form>

                  {/* Social Login Options */}
                  <div className="mt-4 pt-4 border-t border-white/20 space-y-2 text-center">
                    <p className="text-sm text-white/60 mb-1">Or continue with</p>
                    <div className="flex justify-center items-center gap-3">
                      <GooglePopup/>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-white/10 space-y-2 text-center">
                    <Link
                      to="/forgot-password"
                      className="block text-gray-300 hover:text-[#B8460E] text-sm transition-colors font-medium"
                    >
                      Forgot your password?
                    </Link>

                    <p className="text-gray-400 text-sm">
                      Don't have an account?{" "}
                      <Link
                        to="/register"
                        className="text-[#B8460E] hover:text-[#D4541A] transition-colors font-semibold"
                      >
                        Sign up
                      </Link>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Panel - Marketing Content */}
            <div
              ref={rightPanelRef}
              className="w-full lg:w-1/2 bg-gradient-to-br from-[#B8460E] via-[#8B3A0E] to-[#B8460E] p-4 sm:p-6 lg:p-8 flex items-center justify-center relative overflow-hidden order-1 lg:order-2"
            >
              {/* Background decoration */}
              <div className="absolute inset-0 bg-black/10"></div>
              <div className="absolute -top-40 -right-40 w-80 h-80 bg-white/10 rounded-full blur-3xl"></div>
              <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-white/5 rounded-full blur-3xl"></div>

              <div className="relative z-10 text-white text-center max-w-md">
                <div className="mb-4 lg:mb-6">
                  <div className="inline-flex items-center justify-center w-14 h-14 lg:w-16 lg:h-16 bg-white/20 rounded-3xl mb-3 lg:mb-4 backdrop-blur-sm">
                    <TrendingUp className="w-7 h-7 lg:w-8 lg:h-8" />
                  </div>
                  <h2 className="text-xl lg:text-3xl font-bold mb-3 lg:mb-4 leading-tight">
                    Welcome to{" "}
                    <span className="bg-gradient-to-r from-yellow-300 to-pink-300 bg-clip-text text-transparent">
                      Olympus
                    </span>
                  </h2>
                  <p className="text-sm lg:text-lg text-orange-100 leading-relaxed mb-4 lg:mb-6">
                    Discover a revolutionary way for affiliates to earn like never 
                    before. Thousands are already transforming their income with 
                    Olympus - join the elite community today.
                  </p>
                </div>

                {/* Features */}
                <div className="space-y-2 lg:space-y-3 mb-4 lg:mb-6">
                  <div className="flex items-center text-left bg-white/10 backdrop-blur-sm rounded-xl p-2.5 lg:p-3">
                    <Users className="w-4 h-4 lg:w-5 lg:h-5 mr-3 text-orange-300 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-xs lg:text-sm">
                        10,000+ Active Affiliates
                      </h4>
                      <p className="text-xs text-orange-200">
                        Growing community worldwide
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center text-left bg-white/10 backdrop-blur-sm rounded-xl p-2.5 lg:p-3">
                    <Star className="w-4 h-4 lg:w-5 lg:h-5 mr-3 text-orange-300 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-xs lg:text-sm">
                        AI-Powered Analytics
                      </h4>
                      <p className="text-xs text-orange-200">
                        Smart insights for better ROI
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center text-left bg-white/10 backdrop-blur-sm rounded-xl p-2.5 lg:p-3">
                    <TrendingUp className="w-4 h-4 lg:w-5 lg:h-5 mr-3 text-orange-300 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-xs lg:text-sm">
                        Real-time Tracking
                      </h4>
                      <p className="text-xs text-orange-200">
                        Monitor performance instantly
                      </p>
                    </div>
                  </div>
                </div>

                {/* Testimonial */}
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-3 lg:p-4">
                  <div className="flex items-center justify-center mb-2 lg:mb-3">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className="w-3 h-3 text-orange-300 fill-current"
                      />
                    ))}
                  </div>
                  <p className="text-xs lg:text-sm italic mb-2">
                    "Olympus changed everything for me. My affiliate commissions 
                    went from $2K to $8K per month in just 4 months!"
                  </p>
                  <div className="text-xs">
                    <div className="font-semibold">Sarah Johnson</div>
                    <div className="text-blue-200">Top Affiliate Partner</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
